/**
 * skaile rebuild <agent> — re-snapshots all inline-snapshot composition items,
 * bumps the prompt version, and writes compile_manifest.json.
 *
 * This command is the manual "freeze" step for agents that compose
 * inline-snapshot assets (soul, persona). Running it ensures the
 * agent's prompt content is deterministic until the next rebuild.
 */

import path from "node:path";
import { resolveAgentDir } from "@skaile/workspaces/core";
import {
  compileComposition,
  loadAgentManifest,
  type RepoMap,
  resolveMixin,
} from "@skaile/workspaces/runner";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn } from "../helpers.ts";

/**
 * Creates the `skaile rebuild` command.
 *
 * Re-snapshots all inline-snapshot composition items for an agent, bumps the
 * prompt version, and writes `compile_manifest.json`. This is the manual
 * "freeze" step to make an agent's composed prompt deterministic.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#rebuild
 */
export function makeRebuildCommand(): Command {
  return new Command("rebuild")
    .description("Re-snapshot inline-snapshot composition items and bump prompt version")
    .argument("[agent]", "Agent name or path (defaults to current project agent)")
    .option("--project-dir <dir>", "Project directory", process.cwd())
    .action(async (agentArg: string | undefined, opts: { projectDir: string }) => {
      const projectDir = path.resolve(opts.projectDir);

      // Resolve agent directory
      let agentDir: string;
      if (agentArg) {
        // If it looks like a path, use it directly
        if (agentArg.includes("/") || agentArg.includes("\\")) {
          agentDir = path.resolve(projectDir, agentArg);
        } else {
          // Try to resolve as agent name via workspace config
          const resolved = resolveAgentDir(projectDir);
          agentDir = resolved ?? path.resolve(projectDir, agentArg);
        }
      } else {
        const resolved = resolveAgentDir(projectDir);
        if (!resolved) {
          logErr("No agent found in current project. Specify an agent name or path.");
          process.exit(1);
        }
        agentDir = resolved;
      }

      // Load the agent manifest
      const manifest = await loadAgentManifest(agentDir);
      if (!manifest) {
        logErr(`No agent.yaml found in ${agentDir}`);
        process.exit(1);
      }

      if (!manifest.composes || manifest.composes.length === 0) {
        logInfo("Agent has no composes: items — nothing to rebuild.");
        return;
      }

      logInfo(
        `Rebuilding ${manifest.name ?? "agent"} (${manifest.composes.length} composition items)...`,
      );

      // Content loader — resolves asset content from the filesystem via mixin resolver.
      // Phase 1 limitation: only local-path refs (./file.md, /abs/path) resolve
      // successfully. Scoped catalog refs (@publisher/name) require a populated
      // RepoMap from workspace config, which Phase 2 / Library integration will provide.
      // The kind cast is intentional: resolveMixin's signature restricts to 3 kinds but
      // the implementation handles arbitrary strings for catalog lookup. Non-matching
      // kinds simply return null (not found) which surfaces as a compile warning.
      const loadContent = async (ref: string, kind: string): Promise<string | null> => {
        try {
          const repos: RepoMap = {};
          const mixinKind = kind as "persona" | "ruleset" | "knowledge";
          const resolved = await resolveMixin(ref, mixinKind, agentDir, repos);
          return resolved?.content ?? null;
        } catch {
          return null;
        }
      };

      const result = await compileComposition({
        agentDir,
        items: manifest.composes,
        loadContent,
      });

      // Report
      for (const warning of result.warnings) {
        logWarn(warning);
      }

      logOk(
        `Compiled ${result.resolvedCount}/${manifest.composes.length} items → prompt v${result.manifest.prompt_version}`,
      );
      logInfo(`Written: ${result.manifestPath}`);
    });
}
